import React, { createElement } from 'react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { StatusBadge } from '@/components/ui/status-badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import {
  Search,
  Plus,
  Mail,
  Phone,
  FileText,
  MessageSquare,
  Star,
  Circle,
  Send,
  Upload,
  StickyNote,
  Download,
  Paperclip,
  Inbox
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ChatThread, ThreadFilters } from '../types';

const TYPE_ICONS: Record<
  string,
  React.ComponentType<{ className?: string }>
> = {
  phone: Phone,
  letter: FileText,
  portal_message: MessageSquare
};

function getTypeIcon(
  type: string
): React.ComponentType<{ className?: string }> {
  return TYPE_ICONS[type] ?? Mail;
}

interface ChatThreadListProps {
  threads: ChatThread[];
  selectedThreadId?: string | null;
  onThreadSelect: (threadId: string) => void;
  filters: ThreadFilters;
  onFiltersChange: (filters: ThreadFilters) => void;
}

export function ChatThreadList({
  threads,
  selectedThreadId,
  onThreadSelect,
  filters,
  onFiltersChange
}: ChatThreadListProps) {
  const handleSearchChange = (value: string) => {
    onFiltersChange({ ...filters, search: value });
  };

  if (threads.length === 0 && !filters.search) {
    return (
      <div className="flex h-full flex-col">
        <ThreadListHeader onSearchChange={handleSearchChange} />
        <EmptyState />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <ThreadListHeader
        onSearchChange={handleSearchChange}
        searchValue={filters.search}
      />

      <ScrollArea className="flex-1">
        <div className="divide-y">
          {threads.map((thread) => (
            <ThreadItem
              key={thread.id}
              thread={thread}
              isSelected={thread.id === selectedThreadId}
              onSelect={() => onThreadSelect(thread.id)}
            />
          ))}
        </div>

        {threads.length === 0 && filters.search && (
          <div className="p-8 text-center">
            <p className="text-muted-foreground text-sm">
              No conversations match "{filters.search}"
            </p>
          </div>
        )}
      </ScrollArea>

      <ThreadListFooter
        threadCount={threads.length}
        unreadCount={threads.reduce((sum, t) => sum + t.unreadCount, 0)}
      />
    </div>
  );
}

// Extracted sub-components to reduce cognitive load

function ThreadListHeader({
  onSearchChange,
  searchValue = ''
}: {
  onSearchChange: (value: string) => void;
  searchValue?: string;
}) {
  return (
    <div className="space-y-3 border-b p-4">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
          <Input
            placeholder="Search correspondence..."
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-9"
          />
        </div>
        <NewCorrespondenceMenu />
      </div>
    </div>
  );
}

function NewCorrespondenceMenu() {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="icon">
          <Plus className="h-4 w-4" />
          <span className="sr-only">New correspondence</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem>
          <Send className="mr-2 h-4 w-4" />
          Send Email
        </DropdownMenuItem>
        <DropdownMenuItem>
          <Upload className="mr-2 h-4 w-4" />
          Upload Correspondence
        </DropdownMenuItem>
        <DropdownMenuItem>
          <StickyNote className="mr-2 h-4 w-4" />
          Create File Note
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem>
          <Download className="mr-2 h-4 w-4" />
          Download All
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ThreadItem({
  thread,
  isSelected,
  onSelect
}: {
  thread: ChatThread;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const hasUnread = thread.unreadCount > 0;

  return (
    <button
      onClick={onSelect}
      className={cn(
        'w-full p-4 text-left transition-colors',
        'hover:bg-accent/50',
        isSelected && 'bg-primary/10 border-l-primary border-l-4',
        hasUnread && !isSelected && 'bg-info-subtle'
      )}
    >
      <div className="space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-1 items-start gap-3">
            <div
              className={cn(
                'mt-0.5 rounded-lg p-1.5',
                isSelected && 'bg-primary/20',
                !isSelected && hasUnread && 'bg-info-subtle',
                !isSelected && !hasUnread && 'bg-muted'
              )}
            >
              {createElement(getTypeIcon(thread.type), {
                className: 'h-4 w-4'
              })}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h3
                  className={cn(
                    'truncate text-sm',
                    hasUnread ? 'font-semibold' : 'font-medium'
                  )}
                >
                  {thread.subject}
                </h3>
                {thread.lastMessage.direction === 'outbound' &&
                  thread.status === 'sent' && (
                    <Star className="text-warning h-3 w-3 shrink-0 fill-current" />
                  )}
              </div>
              <p className="text-muted-foreground truncate text-xs">
                {thread.institution.name}
              </p>
            </div>
          </div>
          <ThreadMetadata thread={thread} hasUnread={hasUnread} />
        </div>
        <p
          className={cn(
            'line-clamp-2 text-xs',
            hasUnread ? 'text-foreground' : 'text-muted-foreground'
          )}
        >
          {thread.lastMessage.content}
        </p>
      </div>
    </button>
  );
}

function ThreadMetadata({
  thread,
  hasUnread
}: {
  thread: ChatThread;
  hasUnread: boolean;
}) {
  return (
    <div className="flex flex-col items-end gap-1">
      <span className="text-muted-foreground text-xs whitespace-nowrap">
        {formatDate(thread.lastMessage.timestamp)}
      </span>
      <div className="flex items-center gap-1">
        {hasUnread && (
          <>
            <Circle className="text-primary h-2 w-2 fill-current" />
            <Badge variant="secondary" className="bg-primary/10 text-primary">
              {thread.unreadCount}
            </Badge>
          </>
        )}
        <Badge variant="outline">{thread.messages.length}</Badge>
        {thread.hasAttachments && (
          <Badge variant="outline">
            <Paperclip className="h-3 w-3" />
          </Badge>
        )}
        {thread.hasDraft && <StatusBadge tone="caution">Draft</StatusBadge>}
        {thread.needsFollowUp && (
          <StatusBadge tone="critical">Follow-up</StatusBadge>
        )}
      </div>
    </div>
  );
}

function ThreadListFooter({
  threadCount,
  unreadCount
}: {
  threadCount: number;
  unreadCount: number;
}) {
  return (
    <div className="bg-muted/30 border-t p-3">
      <div className="text-muted-foreground flex items-center justify-between text-xs">
        <span>{threadCount} conversations</span>
        <span>{unreadCount} unread</span>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-1 items-center justify-center p-8">
      <div className="text-center">
        <Inbox className="text-muted-foreground mx-auto mb-4 h-12 w-12 opacity-50" />
        <p className="text-muted-foreground text-sm">No correspondence yet</p>
        <p className="text-muted-foreground mt-1 text-xs">
          Start a conversation with the institution
        </p>
      </div>
    </div>
  );
}

function formatDate(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  } else {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  }
}
